/**
 * ImportReview — the step between choosing a DOCUMENT and reading it.
 *
 * Before an order (a PDF, a photo, a Word file) is read, the server works out
 * who sent it and what it is — by KEYWORDS printed on it (the sender's name,
 * GSTIN, email, phone…; the heading "Purchase Order", "Return Claim"), by the
 * sender's records, and by the AI only when those do not answer — and which
 * PROMPT will read it (`data_import.detect_document`). This step shows all of
 * it, with what matched, so the person can check it before anything is read:
 *
 * - the document type and the SENDER. A sender is a record of the screen's
 *   own sender lookup, a record of ANOTHER master found by its name (the
 *   vector index: its object and code), a sender known by NAME with no master
 *   record, or no specific sender at all. "Change" searches every master the
 *   person may read; picking anything — or another type — re-checks at once,
 *   so the prompt on screen is always the one that will read it;
 * - the prompt, editable by people who may manage import profiles (admin,
 *   func-admin, or a role granted it); everyone else sees it read-only. An
 *   edit is used for this file, and — with "Save" — kept for this sender and
 *   type;
 * - the text the keywords were matched in (a scan's transcript);
 * - "Read with the general instructions instead": most documents need nothing
 *   more than the screen's own reading.
 *
 * Shared by every host; the window wires it to the host's transport.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertCircle, ArrowRight, Building2, CheckCircle2, FileSearch, Loader2, Search, Settings2, Sparkles,
  UserRound, UserX,
} from 'lucide-react'
import type {
  ImportKeywordHit, ImportRuleScore, ImportSenderCandidate, ImportSenderPick, ImportSenderSearch,
  RecordImportReadBody, RecordImportReview,
} from './types'

const KIND_LABELS: Record<string, string> = {
  email: 'Email', phone: 'Phone', tax_id: 'Tax ID', name: 'Name', city: 'City', postal: 'PIN / ZIP',
  phrase: 'Phrase', pattern: 'Pattern',
}
const TEXT_SOURCES: Record<string, string> = {
  text_layer: 'the PDF’s text', docx: 'the Word file’s text', transcript: 'text read from the scan',
}

const pretty = (s?: string) => (s || '').replace(/_/g, ' ')

function senderHow(how: string | undefined, senderLabel: string): string {
  switch (how) {
    case 'picked': return 'picked by you'
    case 'password': return 'its stored PDF password opened the file'
    case 'keywords': return 'recognised by keywords on the document'
    case 'master': return `found in the records by the name and details on the document`
    case 'lookup': return `found in the ${senderLabel.toLowerCase()} records by its name`
    case 'known': return 'known to this screen by its name'
    case 'name': return 'known by its name — there is no master record for it'
    case 'ai': case 'found': return 'named by the AI and found in the records'
    default: return ''
  }
}

/** What a sender IS, in words: whose record, or that it is known by name. */
function senderWhat(c: { kind?: string; object?: string; code?: string; ref_code?: string },
  senderLabel: string): string {
  const code = c.ref_code ?? c.code
  if (c.kind === 'name') return 'No master record — known by its name'
  if (c.kind === 'master') return `${pretty(c.object) || 'Master'} · ${code}`
  return `${senderLabel} · ${code}`
}

function typeHow(how?: string): string {
  switch (how) {
    case 'picked': return 'picked by you'
    case 'keywords': return 'recognised by its heading and labels'
    case 'ai': return 'named by the AI — its keywords are kept as a draft'
    default: return ''
  }
}

const CONFIDENCE: Record<string, { text: string; ok: boolean }> = {
  exact: { text: 'Recognised by confirmed keywords.', ok: true },
  likely: { text: 'Recognised. Some keywords are new; they are confirmed when this import is saved unchanged.', ok: true },
  partial: { text: 'Only partly recognised — check the sender and the document type.', ok: false },
  ambiguous: { text: 'Two candidates match equally — pick the right one.', ok: false },
  none: { text: 'Not recognised yet — pick the sender and the document type, or read it with the general instructions.', ok: false },
}

export function ImportReview({
  review, senderLabel, busy, onRecheck, onProcess, onBack, onManage, searchSenders,
}: {
  review: RecordImportReview
  /** What the sender field is called ("Customer"). */
  senderLabel: string
  busy: string | null
  /** Re-check with a picked sender and / or type (no AI for what was picked). */
  onRecheck: (picks: { senderRef?: ImportSenderPick; docType?: string }) => void
  onProcess: (body: RecordImportReadBody) => void
  onBack: () => void
  /** Opens the sender profiles & keywords (managers). */
  onManage?: () => void
  /** Who sent it, by name — every master the person may read. */
  searchSenders?: (q: string) => Promise<ImportSenderSearch>
}) {
  const det = review.detection
  const initialType = det.doc_type?.code || ''
  const [docType, setDocType] = useState(initialType)
  const [prompt, setPrompt] = useState(review.prompt.text || '')
  const [savePrompt, setSavePrompt] = useState(true)
  const [picking, setPicking] = useState(false)

  // A new review (a re-check, another file) resets what is on screen.
  useEffect(() => {
    setDocType(initialType)
    setPrompt(review.prompt.text || '')
    setSavePrompt(true)
    setPicking(false)
  }, [review, initialType])

  const currentRef: ImportSenderPick | undefined = det.no_sender ? { kind: 'none' }
    : det.sender ? { kind: det.sender.kind || 'lookup', key: det.sender.code,
      code: det.sender.ref_code ?? det.sender.code, object: det.sender.object || '',
      display: det.sender.display || det.sender.code } : undefined
  const promptEdited = prompt !== (review.prompt.text || '')
  const typeLabel = useMemo(() => {
    const t = review.doc_types.find((d) => d.code === docType)
    return t?.label || det.doc_type?.label || pretty(docType)
  }, [review.doc_types, docType, det.doc_type])
  const senderName = det.sender ? (det.sender.display || det.sender.code) : ''
  const conf = CONFIDENCE[det.confidence] || CONFIDENCE.none
  const typeCandidates = det.ambiguous?.doc_type ? (det.candidates?.doc_types || []) : []
  const senderCandidates = review.sender_candidates || []

  const pickSender = (ref: ImportSenderPick) => {
    setPicking(false)
    onRecheck({ senderRef: ref, docType: docType || undefined })
  }
  const pickType = (code: string) => {
    setDocType(code)
    onRecheck({ senderRef: currentRef, docType: code || undefined })
  }
  const pickTypeCandidate = (c: ImportRuleScore) => pickType(c.doc_type)

  const process = () => onProcess({
    docType: docType || undefined,
    ...(review.can_edit && promptEdited ? { prompt, savePrompt } : {}),
  })

  const statusBadge = (() => {
    const p = review.prompt
    if (!p.text) return { text: 'No prompt yet', warn: true }
    if (p.scope === 'pending') return { text: 'AI draft — kept once the sender is known', warn: true }
    if (p.source === 'user') return { text: p.updated_by ? `Saved by ${p.updated_by}` : 'Saved by a person', warn: false }
    if (p.status === 'draft') return { text: 'AI draft — check it', warn: true }
    return { text: 'Written by the AI, confirmed by use', warn: false }
  })()

  const scopeNote = (() => {
    const p = review.prompt
    if (p.scope === 'type') return `For ${senderName || 'this sender'} · ${typeLabel}`
    if (p.scope === 'sender') return `${senderName || 'This sender'}'s prompt for any document type`
    if (p.scope === 'type_any') return `For every ${typeLabel || 'document'} — from any sender`
    if (p.scope === 'pending') return 'Drafted from this document'
    return 'Only the screen’s own instructions apply'
  })()

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} aria-label="Check the document">
      <section style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <FileSearch size={16} color="var(--color-accent, #1d4ed8)" />
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Check the document before it is read</h2>
          <div style={{ flex: 1 }} />
          {onManage && (
            <button type="button" style={btn} onClick={onManage} disabled={!!busy}>
              <Settings2 size={14} /> Keywords &amp; prompts…
            </button>
          )}
        </div>
        <div style={{ ...statusLine, color: conf.ok ? '#166534' : '#92400e' }}>
          {conf.ok ? <CheckCircle2 size={15} /> : <AlertCircle size={15} />} {conf.text}
        </div>

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 12 }}>
          <div style={{ ...column, flex: '1 1 280px' }}>
            <label style={field}>Document type
              <select value={docType} onChange={(e) => pickType(e.target.value)} disabled={!!busy}
                style={input} aria-label="Document type">
                <option value="">{review.doc_types.length ? 'Not recognised — pick one' : 'Not recognised'}</option>
                {review.doc_types.map((d) => <option key={d.code} value={d.code}>{d.label}</option>)}
                {docType && !review.doc_types.some((d) => d.code === docType) && (
                  <option value={docType}>{typeLabel}</option>
                )}
              </select>
            </label>
            {det.doc_type && docType === initialType && (
              <Recognised how={typeHow(det.doc_type.how)} hits={det.doc_type.matched} />
            )}
            {typeCandidates.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {typeCandidates.map((c) => (
                  <button key={c.doc_type} type="button" style={btn} disabled={!!busy}
                    onClick={() => pickTypeCandidate(c)}>{c.label || pretty(c.doc_type)}</button>
                ))}
              </div>
            )}
          </div>

          <div style={{ ...column, flex: '1 1 280px' }} aria-label="Sender">
            <div style={field}>From — who sent it</div>
            <div style={senderBox}>
              {det.no_sender ? (
                <SenderLine icon={<UserX size={15} />} title="No specific sender"
                  sub="Read with the prompt for this document type, from any sender" />
              ) : det.sender ? (
                <SenderLine icon={det.sender.kind === 'master' ? <Building2 size={15} /> : <UserRound size={15} />}
                  title={det.sender.display || det.sender.code}
                  sub={senderWhat(det.sender, senderLabel)} />
              ) : (
                <SenderLine icon={<AlertCircle size={15} />} title="Not recognised"
                  sub={senderCandidates.length ? 'Pick one of the records below, or change it' : 'Pick who sent it, or choose no specific sender'} />
              )}
              <button type="button" style={btn} disabled={!!busy} onClick={() => setPicking((v) => !v)}>
                <Search size={14} /> {picking ? 'Close' : det.sender || det.no_sender ? 'Change…' : 'Pick…'}
              </button>
            </div>
            {det.sender && !picking && (
              <Recognised how={senderHow(det.sender.how, senderLabel)} hits={det.sender.matched} />
            )}
            {!det.sender && !det.no_sender && !picking && senderCandidates.length > 0 && (
              <CandidateList items={senderCandidates} senderLabel={senderLabel} busy={!!busy}
                onPick={pickSender} />
            )}
            {picking && (
              <SenderPicker senderLabel={senderLabel} busy={!!busy} search={searchSenders}
                initial={senderCandidates} initialQuery={det.sender?.display || ''}
                onPick={pickSender} />
            )}
          </div>
        </div>

        {(review.notes.length > 0 || review.ai_unavailable) && (
          <ul style={{ margin: '10px 0 0', paddingLeft: 18, color: '#92400e', fontSize: 13 }}>
            {review.ai_unavailable && <li>{review.ai_unavailable}</li>}
            {review.notes.map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        )}
      </section>

      <section style={card} aria-label="Prompt">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>The prompt that will read it</h3>
          <span style={badge(statusBadge.warn)}>{statusBadge.text}</span>
          <div style={{ flex: 1 }} />
          <span style={sub}>{scopeNote}</span>
        </div>
        <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={8}
          readOnly={!review.can_edit} aria-label="Prompt" aria-readonly={!review.can_edit}
          placeholder={review.can_edit
            ? '- Quantities are in BOX unless the line says otherwise.\n- The "Part No." column is their item code.'
            : 'No prompt for this sender and document type yet.'}
          style={{ ...input, marginTop: 8, minHeight: 140, fontFamily: 'inherit', resize: 'vertical',
            background: review.can_edit ? input.background : 'var(--color-bg-secondary, #f5f4f0)' }} />
        {review.can_edit && review.prompt.suggested && review.prompt.suggested !== prompt && (
          <div style={suggestBox}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
              <Sparkles size={14} /> The AI suggests (from a later import)
            </div>
            <pre style={{ margin: '6px 0', whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{review.prompt.suggested}</pre>
            <button type="button" style={btn} onClick={() => setPrompt(review.prompt.suggested || '')}>
              Use the suggestion
            </button>
          </div>
        )}
        {review.can_edit && promptEdited && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, cursor: 'pointer', fontSize: 14 }}>
            <input type="checkbox" checked={savePrompt} onChange={(e) => setSavePrompt(e.target.checked)} />
            Save it for {senderName || (det.no_sender ? 'any sender' : `this ${senderLabel.toLowerCase()}`)}
            {docType ? ` · ${typeLabel}` : ''} — otherwise it is used for this file only
          </label>
        )}
        {!review.can_edit && (
          <div style={{ ...sub, marginTop: 6 }}>
            Administrators, functional administrators and roles allowed to manage import profiles can change it.
          </div>
        )}
        {review.screen_instructions && (
          <details style={{ marginTop: 10 }}>
            <summary style={summary}>The screen’s own instructions (always applied)</summary>
            <pre style={pre}>{review.screen_instructions}</pre>
          </details>
        )}
        {review.text.preview && (
          <details style={{ marginTop: 8 }}>
            <summary style={summary}>
              Text the keywords were matched in — {TEXT_SOURCES[review.text.source] || 'the document'}
              {' '}({review.text.chars.toLocaleString()} characters)
            </summary>
            <pre style={{ ...pre, maxHeight: 280, overflow: 'auto' }}>
              {review.text.preview}{review.text.truncated ? '\n…' : ''}
            </pre>
          </details>
        )}
      </section>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" style={btnFor(!!busy)} disabled={!!busy} onClick={onBack}>Choose another file</button>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" style={btnFor(!!busy)} disabled={!!busy} onClick={() => onProcess({ generic: true })}
            title="Ignore the sender and type; read it with the screen’s own instructions">
            Read with the general instructions
          </button>
          <button type="button" style={btnFor(!!busy, true)} disabled={!!busy} onClick={process}>
            {busy
              ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> {busy}</>
              : <>Process the file <ArrowRight size={14} /></>}
          </button>
        </div>
      </div>
    </div>
  )
}

function SenderLine({ icon, title, sub: subText }: { icon: React.ReactNode; title: string; sub: string }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', minWidth: 0, flex: 1 }}>
      <span style={{ marginTop: 2, color: 'var(--color-text-secondary, #5e6472)' }}>{icon}</span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
        <div style={sub}>{subText}</div>
      </div>
    </div>
  )
}

function candidateWhat(c: ImportSenderCandidate, senderLabel: string): string {
  if (c.kind === 'name') return c.source === 'known' ? 'Known to this screen by its name' : 'No master record — use the name'
  const where = c.kind === 'master' ? pretty(c.object) || 'Master' : senderLabel
  const how = c.source === 'known' ? ' · known to this screen' : ''
  return `${where} · ${c.code}${how}`
}

function CandidateList({ items, senderLabel, busy, onPick }: {
  items: ImportSenderCandidate[]
  senderLabel: string
  busy: boolean
  onPick: (c: ImportSenderPick) => void
}) {
  return (
    <div role="listbox" aria-label="Possible senders" style={listBox}>
      {items.map((c) => (
        <button key={c.key} type="button" role="option" aria-selected={false} style={optionBtn}
          disabled={busy} onClick={() => onPick(c)}>
          {c.kind === 'name' ? <UserRound size={14} /> : <Building2 size={14} />}
          <span style={{ minWidth: 0, textAlign: 'left' }}>
            <span style={{ display: 'block', fontWeight: 500 }}>{c.display}</span>
            <span style={sub}>{candidateWhat(c, senderLabel)}</span>
          </span>
        </button>
      ))}
    </div>
  )
}

function SenderPicker({ senderLabel, busy, search, initial, initialQuery, onPick }: {
  senderLabel: string
  busy: boolean
  search?: (q: string) => Promise<ImportSenderSearch>
  initial: ImportSenderCandidate[]
  initialQuery: string
  onPick: (c: ImportSenderPick) => void
}) {
  const [q, setQ] = useState(initialQuery)
  const [result, setResult] = useState<ImportSenderSearch | null>(null)
  const [searching, setSearching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const seq = useRef(0)

  useEffect(() => {
    const term = q.trim()
    if (!search || term.length < 2) { setResult(null); return }
    const mine = ++seq.current
    const timer = window.setTimeout(() => {
      setSearching(true)
      search(term)
        .then((res) => { if (mine === seq.current) { setResult(res); setError(null) } })
        .catch((e) => {
          console.warn('[ImportReview] sender search failed', e)
          if (mine === seq.current) setError('The search did not answer — try again.')
        })
        .finally(() => { if (mine === seq.current) setSearching(false) })
    }, 300)
    return () => window.clearTimeout(timer)
  }, [q, search])

  const items = result ? result.candidates : (q.trim() ? [] : initial)
  const nameOnly = result?.name_only && !items.some((c) => c.key === result.name_only!.key) ? result.name_only : null
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} disabled={busy}
        placeholder={`Type the sender's name or code — every master is searched`} style={input}
        aria-label="Search for the sender" />
      {searching && <div style={sub}><Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> Searching…</div>}
      {error && <div style={{ ...sub, color: '#b42318' }}>{error}</div>}
      {result && !items.length && !searching && (
        <div style={sub}>No record of any master matches “{result.query}”.</div>
      )}
      {items.length > 0 && <CandidateList items={items} senderLabel={senderLabel} busy={busy} onPick={onPick} />}
      <div role="listbox" aria-label="Other choices" style={listBox}>
        {nameOnly && (
          <button type="button" role="option" aria-selected={false} style={optionBtn} disabled={busy}
            onClick={() => onPick(nameOnly)}>
            <UserRound size={14} />
            <span style={{ textAlign: 'left' }}>
              <span style={{ display: 'block', fontWeight: 500 }}>Use the name “{nameOnly.display}”</span>
              <span style={sub}>There is no master record for this kind of sender — recognise them by name</span>
            </span>
          </button>
        )}
        <button type="button" role="option" aria-selected={false} style={optionBtn} disabled={busy}
          onClick={() => onPick({ kind: 'none' })}>
          <UserX size={14} />
          <span style={{ textAlign: 'left' }}>
            <span style={{ display: 'block', fontWeight: 500 }}>No specific sender</span>
            <span style={sub}>Read it with the prompt for its document type</span>
          </span>
        </button>
      </div>
    </div>
  )
}

function Recognised({ how, hits }: { how: string; hits?: ImportKeywordHit[] }) {
  return (
    <div style={{ fontSize: 13, color: 'var(--color-text-secondary, #5e6472)' }}>
      {how}
      {hits && hits.length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
          {hits.slice(0, 8).map((h, i) => (
            <span key={i} style={chip} title={h.where === 'title' ? 'In a heading' : 'In the text'}>
              {KIND_LABELS[h.kind] || h.kind}: {h.text || h.value}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

const card: React.CSSProperties = {
  background: 'var(--color-bg, #fff)', border: '1px solid var(--color-border, #e6e5e0)',
  borderRadius: 12, padding: 14,
}
const column: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }
const field: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: 'var(--color-text-secondary, #5e6472)',
}
const input: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', minHeight: 32, padding: '6px 8px', fontSize: 14, borderRadius: 7,
  border: '1px solid var(--color-border, #d6d5cf)', background: 'var(--color-bg, #fff)', color: 'inherit',
}
const senderBox: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8,
  border: '1px solid var(--color-border, #d6d5cf)', background: 'var(--color-bg, #fff)',
}
const listBox: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4 }
const optionBtn: React.CSSProperties = {
  display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 10px', borderRadius: 8, fontSize: 14,
  border: '1px solid var(--color-border, #e6e5e0)', background: 'var(--color-bg, #fff)', color: 'inherit',
  cursor: 'pointer', textAlign: 'left',
}
const sub: React.CSSProperties = { fontSize: 13, color: 'var(--color-text-secondary, #5e6472)' }
const statusLine: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 14 }
const chip: React.CSSProperties = {
  fontSize: 13, padding: '1px 8px', borderRadius: 999, background: 'rgba(47,95,227,0.08)',
  color: 'var(--color-text, #15171c)', whiteSpace: 'nowrap', maxWidth: 280, overflow: 'hidden',
  textOverflow: 'ellipsis',
}
const btn: React.CSSProperties = {
  height: 32, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 12px', fontSize: 14,
  fontWeight: 500, borderRadius: 8, border: '1px solid var(--color-border, #e6e5e0)',
  background: 'var(--color-bg, #fff)', color: 'inherit', cursor: 'pointer', flexShrink: 0,
}
const primaryBtn: React.CSSProperties = {
  ...btn, border: '1px solid var(--color-accent, #2f5fe3)', background: 'var(--color-accent, #2f5fe3)', color: '#fff',
}
/** A disabled button LOOKS disabled — never a blue button that does nothing. */
function btnFor(disabled: boolean, primary = false): React.CSSProperties {
  const base = primary ? primaryBtn : btn
  return disabled ? { ...base, opacity: 0.55, cursor: 'not-allowed' } : base
}
const suggestBox: React.CSSProperties = {
  marginTop: 8, padding: 10, borderRadius: 8, fontSize: 13, background: 'rgba(47,95,227,0.06)',
  border: '1px solid rgba(47,95,227,0.25)', color: 'var(--color-text, #15171c)',
}
const summary: React.CSSProperties = { cursor: 'pointer', fontSize: 13, color: 'var(--color-text-secondary, #5e6472)' }
const pre: React.CSSProperties = {
  margin: '6px 0 0', padding: 10, borderRadius: 8, whiteSpace: 'pre-wrap', fontSize: 13,
  fontFamily: 'var(--font-mono, ui-monospace, monospace)', background: 'var(--color-bg-secondary, #f5f4f0)',
}
function badge(warn: boolean): React.CSSProperties {
  return {
    fontSize: 13, padding: '1px 8px', borderRadius: 999,
    background: warn ? 'rgba(217,138,18,0.14)' : 'rgba(22,101,52,0.12)',
    color: warn ? '#a15c07' : '#166534',
  }
}
